import { CheckCircle, Package, Truck, WarningCircle, X } from 'shared/icons';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import { DateTimeText, Id, Mono } from '../../ui/Mono';
import {
    FormatProgressText,
    LoadedChecklistLines,
    MissingChecklistLines,
    ParseCloseBlocks,
    ParseShipmentProgress,
    ShipmentItemStatusVariant,
} from './shipmentModel';

/**
 * O CHECKLIST DO ROMANEIO — a tela onde a trava de 100% fica LEGÍVEL.
 *
 * ── A decisão central desta tela ────────────────────────────────────────────
 * Botão desabilitado sem dizer o que falta é o pior dos mundos: quem está na
 * doca fica olhando um botão cinza e chama o Supervisor. Por isso o que FALTA
 * não é um estado de erro que aparece quando alguém tenta fechar — é uma
 * COLUNA PERMANENTE, item por item, ao lado do que já entrou no veículo.
 *
 * O bloco "Falta bipar" existe SEMPRE:
 *   - com pendências, lista cada pallet/volume pelo código que está na etiqueta
 *     (é o que a pessoa vai procurar no caminhão);
 *   - sem pendências, diz em verde que o embarque pode fechar.
 * Assim a resposta para "por que não fecha?" está na tela antes da pergunta.
 *
 * ── O que esta tela NÃO faz ─────────────────────────────────────────────────
 * Não recalcula progresso nem decide se pode fechar: `progress`, `can_close` e
 * `close_blocks` vêm prontos do servidor (motor puro `shipmentChecklist.js`).
 * Uma segunda conta aqui seria a que um dia discorda da que baixa o estoque.
 */

const KIND_ICON = { LPN: Package, VOLUME: Truck };

/** Uma linha do checklist. `code` é a etiqueta — vai grande e monoespaçado. */
const ChecklistRow = ({ line, label, onRemove = null, removing = false }) => {
    const Icon = KIND_ICON[line.kind] || Package;
    return (
        <li className="flex items-center gap-3 rounded-lg border border-mono-200 bg-white px-3 py-2 dark:border-mono-800 dark:bg-mono-950">
            <Icon size={18} className="shrink-0 text-mono-500" />
            <span className="min-w-0 flex-1">
                <Id value={line.code} className="block text-sm font-bold" />
                <span className="text-xs text-mono-500 dark:text-mono-400">
                    {label(`wms.shipment_item_kind.${line.kind}`)}
                    {/* DE ONDE o pallet saiu — a posição no instante do bipe
                        (13/08). Aparece sem rótulo, junto do resto da assinatura
                        da leitura: o código de endereço se explica sozinho para
                        quem lê esta tela, e ele NÃO é pendência quando falta
                        (volume não tem posição no armazém). */}
                    {line.scanned_location_code ? (
                        <>
                            {' · '}
                            <Mono>{line.scanned_location_code}</Mono>
                        </>
                    ) : null}
                    {line.scanned_at ? (
                        <>
                            {' · '}
                            <DateTimeText value={line.scanned_at} relative />
                        </>
                    ) : null}
                    {line.scan_method ? ` · ${label(`wms.collector.scan_method.${line.scan_method}`)}` : ''}
                </span>
            </span>
            {onRemove ? (
                <button
                    type="button"
                    onClick={() => onRemove(line)}
                    disabled={removing}
                    title={label('wms.shipment.remove_item')}
                    aria-label={`${label('wms.shipment.remove_item')} ${line.code || ''}`}
                    className="shrink-0 rounded p-1 text-mono-500 transition-colors hover:bg-mono-100 disabled:opacity-40 dark:hover:bg-mono-900"
                >
                    <X size={14} />
                </button>
            ) : null}
        </li>
    );
};

/**
 * A BARRA DE PROGRESSO. O número ("7 de 9 bipados") vem antes da barra porque é
 * ele que se lê de longe; a barra é só a leitura periférica. Enquanto faltar
 * item a cor é âmbar — verde só quando a trava realmente liberou, senão a tela
 * anuncia sucesso antes de existir.
 */
export const ShipmentProgressMeter = ({ shipment, label, className = '' }) => {
    const progress = ParseShipmentProgress(shipment);
    const complete = progress.complete;
    return (
        <div className={`space-y-1.5 ${className}`}>
            <div className="flex items-baseline justify-between gap-3">
                <span className="text-lg font-black text-black dark:text-white">
                    {FormatProgressText(progress, label)}
                </span>
                <span className="text-sm font-semibold text-mono-500 dark:text-mono-400">{`${progress.percent}%`}</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-mono-200 dark:bg-mono-800">
                <div
                    className={`h-full rounded-full transition-all duration-300 ${
                        complete ? 'bg-success-600 dark:bg-success-500' : 'bg-warning-500 dark:bg-warning-400'
                    }`}
                    style={{ width: `${progress.total === 0 ? 0 : progress.percent}%` }}
                />
            </div>
        </div>
    );
};

/**
 * A LISTA DO QUE FALTA — o componente que o console e o coletor compartilham.
 * Um lugar só para "o que falta" impede as duas telas de contarem diferente.
 */
export const MissingItemsList = ({ shipment, label, emptyTone = 'success', className = '' }) => {
    const missing = MissingChecklistLines(shipment);
    const progress = ParseShipmentProgress(shipment);

    if (progress.total === 0) {
        return (
            <p className={`rounded-lg bg-warning-soft px-3 py-3 text-sm font-medium text-warning-text dark:bg-warning-900/30 dark:text-warning-200 ${className}`}>
                {label('wms.shipment.empty_checklist')}
            </p>
        );
    }

    if (missing.length === 0) {
        // Pendência ZERO com total > 0 é o único caso em que esta tela pode
        // dizer "pode fechar" — e ela usa a MESMA fonte da trava (`complete`).
        return progress.complete ? (
            <p
                className={`flex items-center gap-2 rounded-lg px-3 py-3 text-sm font-semibold ${
                    emptyTone === 'success'
                        ? 'bg-success-soft text-success-text dark:bg-success-900/30 dark:text-success-200'
                        : 'bg-mono-100 text-mono-700 dark:bg-mono-900 dark:text-mono-300'
                } ${className}`}
            >
                <CheckCircle size={18} className="shrink-0" />
                {label('wms.shipment.missing_none')}
            </p>
        ) : null;
    }

    return (
        <ul className={`space-y-2 ${className}`}>
            {missing.map((line, index) => (
                <ChecklistRow key={line.id || `${line.code}-${index}`} line={line} label={label} />
            ))}
        </ul>
    );
};

/**
 * Os motivos do servidor, na íntegra. São eles que citam O QUE falta ("Faltam 2
 * de 9 … MP-260812-001, PA-260812-004") — a mesma frase que o 409 do
 * fechamento traz, para a tela e o servidor nunca contarem histórias diferentes.
 */
export const CloseBlocksNotice = ({ source, label, className = '' }) => {
    const blocks = ParseCloseBlocks(source);
    if (blocks.length === 0) {
        return null;
    }
    return (
        <div
            role="status"
            className={`space-y-2 rounded-xl border-2 border-warning-400 bg-warning-soft px-4 py-3 dark:border-warning-700 dark:bg-warning-900/30 ${className}`}
        >
            <p className="flex items-center gap-2 text-sm font-bold text-warning-text dark:text-warning-200">
                <WarningCircle size={18} className="shrink-0" />
                {label('wms.shipment.close_blocked_title')}
            </p>
            <ul className="space-y-1.5">
                {blocks.map((block, index) => (
                    <li key={`${block.rule}-${index}`} className="text-sm text-warning-text dark:text-warning-200">
                        <span className="font-medium leading-snug">{block.message}</span>
                        <Mono className="ml-2 text-xs opacity-70">{block.rule}</Mono>
                    </li>
                ))}
            </ul>
        </div>
    );
};

/**
 * O painel inteiro: progresso, o que falta (esquerda) e o que já entrou
 * (direita). Duas colunas lado a lado, e não uma lista única com badges — a
 * pergunta da doca é sempre "o que ainda falta", e ela merece uma coluna
 * própria em vez de ficar misturada com o que já foi feito.
 */
const ShipmentChecklistPanel = ({ shipment, label, onRemoveItem = null, removingItemId = null, canManage = false }) => {
    const progress = ParseShipmentProgress(shipment);
    const missing = MissingChecklistLines(shipment);
    const loaded = LoadedChecklistLines(shipment);
    const isOpen = shipment?.status === 'OPEN';

    return (
        <div className="space-y-4">
            <ShipmentProgressMeter shipment={shipment} label={label} />

            {isOpen && shipment?.can_close === false ? <CloseBlocksNotice source={shipment} label={label} /> : null}

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <section className="space-y-2">
                    <h4 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-mono-600 dark:text-mono-300">
                        {label('wms.shipment.missing_title')}
                        <Badge variant={missing.length > 0 ? 'warning' : 'success'}>{progress.pending}</Badge>
                    </h4>
                    <MissingItemsList shipment={shipment} label={label} />
                </section>

                <section className="space-y-2">
                    <h4 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-mono-600 dark:text-mono-300">
                        {label('wms.shipment.loaded_title')}
                        <Badge variant={loaded.length > 0 ? 'success' : 'neutral'}>{progress.loaded}</Badge>
                    </h4>
                    {loaded.length === 0 ? (
                        <p className="rounded-lg bg-mono-50 px-3 py-3 text-sm text-mono-500 dark:bg-mono-900 dark:text-mono-400">
                            {label('wms.shipment.loaded_none')}
                        </p>
                    ) : (
                        <ul className="space-y-2">
                            {loaded.map((line) => (
                                <li key={line.id} className="flex items-center gap-2">
                                    <div className="min-w-0 flex-1">
                                        <ChecklistRow line={line} label={label} />
                                    </div>
                                    <Badge variant={ShipmentItemStatusVariant(line.status)}>
                                        {label(`wms.shipment_item_status.${line.status}`)}
                                    </Badge>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>

            {/* Remover pendência do checklist é a válvula que impede o beco sem
                saída: um pallet acrescentado por engano tornaria a trava de 100%
                impossível de satisfazer com o caminhão esperando. Só aparece em
                embarque ABERTO, e só nas linhas que ainda não foram bipadas —
                item no veículo é fato registrado, e fato não se apaga. */}
            {canManage && isOpen && missing.length > 0 && onRemoveItem ? (
                <div className="space-y-2 rounded-xl border border-mono-200 p-3 dark:border-mono-800">
                    <p className="text-xs font-semibold uppercase tracking-wide text-mono-500">
                        {label('wms.shipment.remove_item')}
                    </p>
                    <ul className="flex flex-wrap gap-2">
                        {missing
                            .filter((line) => line.id)
                            .map((line) => (
                                <li key={`remove-${line.id}`}>
                                    <Button
                                        size="sm"
                                        variant="secondary"
                                        loading={removingItemId === line.id}
                                        disabled={Boolean(removingItemId)}
                                        onClick={() => onRemoveItem(line)}
                                    >
                                        <X size={14} className="mr-1.5" />
                                        <Mono>{line.code}</Mono>
                                    </Button>
                                </li>
                            ))}
                    </ul>
                </div>
            ) : null}
        </div>
    );
};

export default ShipmentChecklistPanel;
